// Nested Commodity Selector Component
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, Search, Leaf, Apple } from 'lucide-react';
import { useCommodities } from '../../utils/api-hooks/commodity';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';
import FloatingLabel from '../../components/FloatingLabel';
import {
  FILTER_CHEVRON_CLASS,
  FILTER_CHEVRON_SIZE,
  FILTER_LABEL_CLASS,
  FILTER_PLACEHOLDER_TEXT_CLASS,
  FILTER_VALUE_CLASS,
  FILTER_VALUE_TEXT_CLASS,
  filterTriggerClass,
} from './filterControlStyles';

const MENU_GAP = 4;
const MENU_MIN_WIDTH = 248;
const MENU_PREFERRED_HEIGHT = 380;

/**
 * NestedCommoditySelector - A dropdown with grouped/nested commodity options
 * Uses useCommodities hook to fetch commodities data automatically
 * @param {Object} props
 * @param {string} props.value - Currently selected commodity (lowercase)
 * @param {function} props.onChange - Callback when selection changes (receives lowercase value)
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Disabled state
 * @param {'floating'|'stacked'} props.variant - `floating` keeps the label inside
 *   the control (legacy Terminal/Map strips); `stacked` renders the label above a
 *   compact, unfilled control (Analytics top filter bar design).
 * @param {string} props.heightClass - `stacked` only: overrides the 32px control
 *   height so callers can size it to a neighbouring control (the mobile top bar
 *   matches the hamburger button).
 * @param {boolean} props.portalMenu - Render the open menu in a body portal with
 *   fixed positioning so it is not clipped by an `overflow` ancestor (modals).
 *   Off by default; Analytics keeps the in-flow absolute menu.
 */
const NestedCommoditySelector = ({
  value,
  onChange,
  placeholder = 'Select commodity',
  disabled = false,
  label = '',
  variant = 'floating',
  heightClass = 'h-[32px]',
  portalMenu = false,
}) => {
  const { fontSize } = useGlobalFontSize();
  // Fetch commodities using custom hook
  const { commodities, isLoading, isError } = useCommodities();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const listContainerRef = useRef(null);
  const selectedItemRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const getCommodityLabel = (commodity) => (
    commodity?.toLowerCase() === 'kiwifruit' ? 'Kiwis' : commodity
  );

  // Get category icon
  const getCategoryIcon = (category) => {
    const iconProps = { size: 14, className: 'mr-2 text-green-400' };
    switch (category.toLowerCase()) {
      case 'fruits':
        return <Apple {...iconProps} />;
      case 'vegetables':
        return <Leaf {...iconProps} />;
      default:
        return <Leaf {...iconProps} />;
    }
  };

  // Find the display name for current value
  const getDisplayName = () => {
    if (!value) return placeholder;
    
    for (const [category, items] of Object.entries(commodities)) {
      const found = items.find(item => item.toLowerCase() === value.toLowerCase());
      if (found) return getCommodityLabel(found);
    }
    return getCommodityLabel(value.charAt(0).toUpperCase() + value.slice(1));
  };

  // Find which category the current value belongs to
  const getCurrentCategory = () => {
    if (!value) return null;
    
    for (const [category, items] of Object.entries(commodities)) {
      if (items.some(item => item.toLowerCase() === value.toLowerCase())) {
        return category;
      }
    }
    return null;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current?.contains(event.target);
      const inPortalMenu = menuRef.current?.contains(event.target);
      if (dropdownRef.current && !inTrigger && !inPortalMenu) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    // Capture phase: a host (e.g. the alerts modal panel) may stop mousedown
    // from bubbling, which would otherwise leave the menu open.
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  // Portal mode: pin the menu to the trigger (fixed), flipping above when
  // there is no room below, and follow resize / any ancestor scroll.
  useLayoutEffect(() => {
    if (!portalMenu || !isOpen) return undefined;
    const updatePosition = () => {
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - 8;
      const spaceAbove = rect.top - MENU_GAP - 8;
      const openUp = spaceBelow < MENU_PREFERRED_HEIGHT && spaceAbove > spaceBelow;
      const maxHeight = Math.max(160, Math.min(MENU_PREFERRED_HEIGHT, openUp ? spaceAbove : spaceBelow));
      const width = Math.max(rect.width, MENU_MIN_WIDTH);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setMenuStyle({
        position: 'fixed',
        left,
        width,
        zIndex: 100,
        maxHeight,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + MENU_GAP }
          : { top: rect.bottom + MENU_GAP }),
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [portalMenu, isOpen]);

  // The portal menu mounts one render after `isOpen` (it waits for its first
  // measured position), so focus/scroll must key off the menu being rendered.
  const menuReady = isOpen && (!portalMenu || Boolean(menuStyle));

  // Focus search input and scroll to selected item when dropdown opens
  useEffect(() => {
    if (menuReady) {
      // Focus search input
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
      
      // Scroll to selected item after a brief delay to ensure DOM is ready
      setTimeout(() => {
        if (selectedItemRef.current && listContainerRef.current) {
          // Keep scrolling scoped to the dropdown list container (avoid page-level scroll)
          const container = listContainerRef.current;
          const item = selectedItemRef.current;
          const target =
            item.offsetTop - container.clientHeight / 2 + item.clientHeight / 2;
          container.scrollTop = Math.max(
            0,
            Math.min(container.scrollHeight - container.clientHeight, target)
          );
        }
      }, 50);
    }
  }, [menuReady]);

  // Initialize expanded categories (expand the one containing current value)
  useEffect(() => {
    const currentCategory = getCurrentCategory();
    if (currentCategory) {
      setExpandedCategories(prev => ({ ...prev, [currentCategory]: true }));
    }
  }, [value, commodities]);

  // Toggle category expansion
  const toggleCategory = (category) => {
    const isExpanding = !expandedCategories[category];

    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));

    // When expanding, scroll to position category header in middle of dropdown
    if (isExpanding) {
      setTimeout(() => {
        const container = listContainerRef.current;
        if (!container) return;

        const { scrollHeight, clientHeight } = container;

        // Only scroll if content overflows
        if (scrollHeight > clientHeight) {
          // Find the category header and position it in the middle
          const categoryHeader = container.querySelector(`[data-category="${category}"]`);
          if (categoryHeader) {
            // Scroll so header is roughly 1/3 from the top of visible area
            const headerOffset = categoryHeader.offsetTop;
            const targetScroll = Math.max(0, headerOffset - clientHeight / 3);

            container.scrollTo({
              top: targetScroll,
              behavior: 'auto'
            });
          }
        }
      }, 100);
    }
  };

  // Handle commodity selection
  const handleSelect = (commodity) => {
    onChange(commodity.toLowerCase());
    setIsOpen(false);
    setSearchQuery('');
  };

  // Sort commodities alphabetically (categories and items within each category)
  const getSortedCommodities = (commoditiesObj) => {
    const sorted = {};
    // Sort categories alphabetically
    const sortedCategories = Object.keys(commoditiesObj).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );

    for (const category of sortedCategories) {
      // Sort items within each category alphabetically
      sorted[category] = [...commoditiesObj[category]].sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      );
    }
    return sorted;
  };

  // Filter commodities based on search
  const getFilteredCommodities = () => {
    const sortedCommodities = getSortedCommodities(commodities);

    if (!searchQuery.trim()) return sortedCommodities;

    const filtered = {};
    const query = searchQuery.toLowerCase();

    for (const [category, items] of Object.entries(sortedCommodities)) {
      const filteredItems = items.filter(item =>
        item.toLowerCase().includes(query)
      );
      if (filteredItems.length > 0) {
        filtered[category] = filteredItems;
      }
    }

    return filtered;
  };

  const filteredCommodities = getFilteredCommodities();
  const hasResults = Object.keys(filteredCommodities).length > 0;

  // Expand all categories when searching
  useEffect(() => {
    if (searchQuery) {
      const allExpanded = {};
      Object.keys(filteredCommodities).forEach(cat => {
        allExpanded[cat] = true;
      });
      setExpandedCategories(allExpanded);
    }
  }, [searchQuery]);

  const wrapMenu = (menu) => (portalMenu ? createPortal(menu, document.body) : menu);

  const isStacked = variant === 'stacked';

  if (isLoading) {
    return (
      <div className="relative w-full">
        {isStacked && label ? (
          <label className={FILTER_LABEL_CLASS}>{label}</label>
        ) : null}
        <div
          className={
            isStacked
              ? `w-full min-w-[180px] ${FILTER_PLACEHOLDER_TEXT_CLASS} ${filterTriggerClass({ disabled: true, heightClass })}`
              : 'bg-white dark:bg-[#282a29] border border-[#B1B9B4] rounded-[8px] h-8 px-2 sm:px-3 w-full min-w-[180px] text-[#444746] dark:text-[#f0f5f2] font-medium flex items-center justify-between'
          }
          style={isStacked ? undefined : { fontSize: fontSize - 2 }}
        >
          <span>Loading...</span>
          <div className="animate-spin h-4 w-4 border-2 border-bloom-green rounded-full border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {isStacked ? (
        label ? (
          <label className={FILTER_LABEL_CLASS}>{label}</label>
        ) : null
      ) : (
        <FloatingLabel
          label={label}
          isFocused={isOpen}
          hasValue={!!value}
        />
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={
          isStacked
            ? `w-full min-w-[180px] ${filterTriggerClass({ disabled, open: isOpen, heightClass })}`
            : `
          bg-white dark:bg-[#282a29] rounded-[8px] h-8 px-2 sm:px-3 w-full min-w-[180px]
          flex items-center gap-2
          border transition-colors duration-150
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-[#333534] cursor-pointer'}
          ${isOpen ? 'border-[#16A34A]' : 'border-[#B1B9B4] hover:border-[#16A34A]'}
        `
        }
      >
        <span
          className={
            isStacked
              ? `${FILTER_VALUE_CLASS} ${
                  value ? FILTER_VALUE_TEXT_CLASS : FILTER_PLACEHOLDER_TEXT_CLASS
                }`
              : `font-medium min-w-0 flex-1 truncate text-left ${
                  !value && label ? 'text-transparent' : 'text-[#444746] dark:text-[#f0f5f2]'
                }`
          }
          style={isStacked ? undefined : { fontSize: fontSize - 2 }}
        >
          {getDisplayName()}
        </span>
        <ChevronDown
          size={isStacked ? FILTER_CHEVRON_SIZE : 16}
          className={
            isStacked
              ? `${FILTER_CHEVRON_CLASS} ${isOpen ? 'rotate-180' : ''}`
              : `text-[#828382] flex-shrink-0 ml-auto transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`
          }
        />
      </button>

      {/* Dropdown Menu */}
      {menuReady && wrapMenu(
        <div
          ref={menuRef}
          style={portalMenu ? menuStyle : undefined}
          className={`${portalMenu ? 'flex flex-col' : 'absolute z-50 mt-1 w-full sm:w-[248px] min-w-[248px] max-w-[calc(100vw-24px)] sm:max-w-none'} bg-white dark:bg-[#0B0F0C] border border-gray-200 dark:border-[#262B27] rounded-[12px] shadow-xl overflow-hidden p-[6px]`}
        >
          {/* Search Input */}
          <div className="pb-[6px]">
            <div className="relative">
              <Search size={14} className="absolute left-[10px] top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search commodities..."
                className="w-full bg-[#F5F6F5] dark:bg-[#121513] border border-[#E2E2E2] dark:border-[#262B27] rounded-[10px] px-3 py-2 pl-8 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-[#16A34A] dark:focus:border-[#16A34A]"
                style={{ fontSize: fontSize - 2 }}
              />
            </div>
          </div>

          {/* Commodity List */}
          <div
            ref={listContainerRef}
            className={portalMenu ? 'min-h-0 flex-1 overflow-y-auto' : 'max-h-[300px] overflow-y-auto'}
          >
            {!hasResults ? (
              <div 
                className="px-3 py-4 text-center text-[#828382] dark:text-gray-500"
                style={{ fontSize: fontSize - 2 }}
              >
                No commodities found
              </div>
            ) : (
              Object.entries(filteredCommodities).map(([category, items]) => (
                <div key={category} className="pb-[2px] last:pb-0">
                  {/* Category Header */}
                  <button
                    type="button"
                    data-category={category}
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between rounded-[9px] px-3 py-2 bg-gray-100 dark:bg-[#121513] hover:bg-gray-200 dark:hover:bg-[#1a1f1b] transition-colors"
                  >
                    <div className="flex items-center">
                      {getCategoryIcon(category)}
                      <span
                        className="font-medium text-gray-700 dark:text-gray-300"
                        style={{ fontSize: fontSize - 2 }}
                      >
                        {category}
                      </span>
                      <span
                        className="ml-2 text-gray-400 dark:text-gray-500"
                        style={{ fontSize: fontSize - 4 }}
                      >
                        ({items.length})
                      </span>
                    </div>
                    <ChevronRight
                      size={14}
                      className={`text-gray-400 dark:text-gray-500 transition-transform duration-200 ${expandedCategories[category] ? 'rotate-90' : ''}`}
                    />
                  </button>

                  {/* Category Items */}
                  {expandedCategories[category] && (
                    <div className="py-1">
                      {items.map((commodity) => {
                        const isSelected = commodity.toLowerCase() === value?.toLowerCase();
                        return (
                          <button
                            key={commodity}
                            ref={isSelected ? selectedItemRef : null}
                            type="button"
                            onClick={() => handleSelect(commodity)}
                            className={`
                              w-full text-left rounded-[9px] px-3 py-2 transition-colors
                              ${isSelected
                                ? 'bg-[#E7F5EC] dark:bg-[#17311F] text-[#16A34A] font-medium'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#121513] hover:text-gray-900 dark:hover:text-white'
                              }
                            `}
                            style={{ fontSize: fontSize - 2 }}
                          >
                            <span className="block truncate">{getCommodityLabel(commodity)}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NestedCommoditySelector;

